/**
 * Apply a skin to the document.
 * AccessHub Neon packs apply via suiteBridge when id is not builtin.
 */

import { DEFAULT_THEME_ID, getTheme, isThemeId } from "./registry";
import { syncMusicTokens } from "./runtime";
import { resolveSuitePack, selectSuiteOrBuiltin, wireSuiteSkins } from "./suiteBridge";
import type { ThemeId } from "./types";

const STORAGE_KEY = "musicforge.skin.v1";

export function applyTheme(themeId: ThemeId | string): void {
  if (!isThemeId(String(themeId)) && resolveSuitePack(String(themeId))) {
    selectSuiteOrBuiltin(String(themeId), {
      system: "pf",
      storageKey: STORAGE_KEY,
      applyBuiltin: applyBuiltinTheme,
    });
    return;
  }
  applyBuiltinTheme(themeId);
}

function applyBuiltinTheme(themeId: ThemeId | string): void {
  const theme = getTheme(themeId);
  const root = document.documentElement;

  root.setAttribute("data-theme", theme.id);
  root.setAttribute("data-skin", theme.id);
  root.classList.toggle("dark", theme.mode === "dark");
  root.classList.toggle("light", theme.mode === "light");
  root.classList.toggle("pf-light", theme.mode === "light");

  for (const [key, value] of Object.entries(theme.tokens)) {
    root.style.setProperty(key, value);
  }
  syncMusicTokens(root);

  let meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", "theme-color");
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", theme.tokens["--pf-bg-deep"]);
}

export function getStoredThemeId(): ThemeId {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw && isThemeId(raw)) return raw;
    if (raw && resolveSuitePack(raw)) return raw as ThemeId;
  } catch {
    /* ignore */
  }
  return DEFAULT_THEME_ID;
}

export function setStoredThemeId(id: ThemeId | string): ThemeId {
  const theme = getTheme(id);
  try {
    localStorage.setItem(STORAGE_KEY, theme.id);
  } catch {
    /* ignore */
  }
  return theme.id as ThemeId;
}

export function selectTheme(id: ThemeId | string): ThemeId {
  if (resolveSuitePack(String(id))) {
    selectSuiteOrBuiltin(String(id), {
      system: "pf",
      storageKey: STORAGE_KEY,
      applyBuiltin: applyBuiltinTheme,
    });
    return id as ThemeId;
  }
  const resolved = setStoredThemeId(id);
  applyTheme(resolved);
  return resolved;
}

export function bootstrapTheme(): ThemeId {
  const id = getStoredThemeId();
  applyTheme(id);
  wireSuiteSkins({
    system: "pf",
    storageKey: STORAGE_KEY,
    applyBuiltin: applyBuiltinTheme,
    getDefault: () => DEFAULT_THEME_ID,
    apiBaseUrl:
      (import.meta.env.VITE_PROXYPRO_URL as string | undefined) ||
      "https://proxy.vividlex.com",
  });
  return id;
}
